import { Footnote, FootnotesState } from './FootnotesReducerTypes';
import {
  connectionName,
  getFootnotesConnection as setUpFootnotesConnection,
  FootnotesDispatchers,
} from './FootnotesReducer';
import { SeamlessCoreStore } from '@seamless/store';
import { getStore } from '@/store';
import Logger from '@owc-utils/common/Logger';
import { getFootnotesIfAvailable } from './FootnotesUtils';
import { BaseDataJson } from '@owc-types/BaseDataJSON';
/**
 * add a connection to the seamless store
 *
 * @function addFootnotesConnection
 * @returns seamlessStore
 */
const addFootnotesConnection = async (): Promise<void> => {
  const store = await getStore();
  const connection = await setUpFootnotesConnection();
  return store.addConnection(connection).catch(() => Logger.debug('connection already added'));
};

/**
 * the connection from the FootnotesConnection
 *
 * @function getFootnotesConnection
 * @return Public dispatchers
 */
const getFootnotesConnection = async (): Promise<FootnotesDispatchers> => {
  const store = await getStore();
  await addFootnotesConnection();
  return store.getConnectionDispatchers(connectionName);
};

let store: SeamlessCoreStore;

const GLOBALELEM_FOOTNOTE_REFERENCE_COMPONENT = 'globalelem-footnote-reference-component';

//This is the list of components whose footnotes should be listed in their immediate parent
const NESTED_FOOTNOTES = [
  'OWC-MEDIA-SLIDER-ITEM',
  'OWC-MEDIA-GALLERY-ITEM',
  'OWC-SIMPLE-TEASER-ITEM',
  'OWC-FEATURE-BOX-ITEM',
  'OWC-TABLE-CELL',
];

//This is the list of components whose footnotes should be local when in mobile (inside modals)
const LOCAL_FOOTNOTES = ['OWC-TABLE-CELL', 'OWC-HOTSPOT-SIMPLE'];

/**
 * Adds a list of footnotes to the store
 * ```js
 * import { addFootnotesToStore } from '@owc-utils/Footnotes/FootnotesHelper';
 * addFootnotesToStore(this.footnotesData.footnotes);
 * ```
 * @exports
 * @function addFootnotesToStore
 * @param footnotes
 * @returns Promise
 */
export const addFootnotesToStore = async (footnotes: Footnote[]) => {
  const footnotesConnection = await getFootnotesConnection();
  footnotesConnection.addFootnotes(footnotes);
};

export const getLocalFootnotes = (data?: BaseDataJson): Footnote[] | false => {
  const footnotes = getFootnotesIfAvailable(data)?.footnotes?.filter(footnote => footnote.local === true);
  return footnotes && footnotes.length > 0 ? footnotes : false;
};

const referencedFootnoteIds: Record<string, string>[] = [];

const setUniqueReference = (footnoteId: string): string => {
  const uniqueId: number = referencedFootnoteIds.filter((el: Record<string, string>) => el.id === footnoteId)
    .length;
  return `${uniqueId + 1}`;
};

//Sets the footnote reference of the globalElement footnote reference component
export const setReference = (component: any, footnoteId: string): string => {
  const { tagName, componentId } = component.$el.getRootNode().host;
  const moduleId = `${tagName
    .replace('owc-', '')
    .split('-')
    .join('')}`;
  const reference = `owc_${moduleId}${setUniqueReference(footnoteId)}_${componentId}`;
  return reference.toLowerCase();
};

export const isNested = (componentElement: any) => NESTED_FOOTNOTES.includes(componentElement.tagName);

//Sets the footnote moduleId of the globalElement footnote reference component
export const setModuleId = (component: any): string => {
  if (component.$el) {
    const componentElement = component.$el.getRootNode().host;

    const moduleId = `owc-${
      isNested(componentElement)
        ? component.parentId || componentElement.parentElement?.componentId
        : componentElement.componentId
    }`;

    return moduleId;
  }
  return '';
};

/**
 * Mounts the footnotes elements inside the component (with <sup> element)
 * ```js
 * import { mountFootnotes } from '@owc-utils/Footnotes/FootnotesHelper';
 * mountFootnotes(domElement);
 
 * ```
 * eg.
 * <p>Your text goes here with <span data-footnote-id="abc123xyz">footnote</span></p>
 * turns into
 * <p>Your text goes here with <span data-footnote-id="abc123xyz">footnote<sup>1</sup></span></p>
 * @function mountFootnotes
 * @exports
 * @param componentElement
 * @param isInModal - optional parameter to set the footnotes as local when inside modals
 */

export const mountFootnotes = async (component: any, isInModal?: boolean) => {
  //TODO: ??=
  if (!store) {
    store = await getStore();
  }

  const footnoteElements: HTMLElement[] = Array.from(component.$el.querySelectorAll('[data-footnote-id]'));

  // Don't subscribe if there's no footnote elements
  if (footnoteElements.length === 0) return;

  store.subscribe(connectionName, ({ footnotes: allFootnotes }: FootnotesState) => {
    footnoteElements.forEach(footnote => {
      const currentFootnoteId = footnote.dataset.footnoteId as string;
      const currentFootnote = allFootnotes[currentFootnoteId];
      const reference = footnote.querySelector(GLOBALELEM_FOOTNOTE_REFERENCE_COMPONENT);
      const fromLocalFootnotes = typeof isInModal == 'boolean';

      if (typeof currentFootnote !== 'object') return;

      if ((currentFootnote && !reference) || fromLocalFootnotes) {
        const { text, local } = currentFootnote;
        const forceLocalFootnote = fromLocalFootnotes
          ? LOCAL_FOOTNOTES.includes(component.$el.getRootNode().host.tagName) && isInModal
          : footnote.closest('[data-is-in-modal=true]');

        const newReference = `<${GLOBALELEM_FOOTNOTE_REFERENCE_COMPONENT} 
          content="${text.replace(/"/g, '&quot;')}"
          reference="${setReference(component, currentFootnoteId)}"
          ${local || forceLocalFootnote ? `module-id="${setModuleId(component)}"` : ''}
        ></${GLOBALELEM_FOOTNOTE_REFERENCE_COMPONENT}>`;

        if (reference) {
          reference.outerHTML = '';
        }
        footnote.insertAdjacentHTML('beforeend', newReference);

        referencedFootnoteIds.push({ id: currentFootnoteId });
      }
    });
  });
};
